// external
import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
} from "react";

// state management
import { FaoBuilderContext } from "./FaoBuilderContext";

// reducers
import {
  nonModeledReducer,
  initialNonModeledData,
} from "reducers/non-modeled-reducer";
import {
  costOfAttendanceReducer,
  initialCostOfAttendance,
} from "reducers/cost-of-attendance-reducer";

export const FaoResultsContext = createContext();

export function FaoResultsStore({ children }) {
  const { modelConfig } = useContext(FaoBuilderContext);
  const [nonModeledData, nonModeledDispatch] = useReducer(
    nonModeledReducer,
    initialNonModeledData
  );
  const [costOfAttendance, costOfAttendanceDispatch] = useReducer(
    costOfAttendanceReducer,
    initialCostOfAttendance
  );

  // Admit adjustment data state
  const [admitAdjustmentData, setAdmitAdjustmentData] = useState({});
  const [admitAdjustmentTabs, setAdmitAdjustmentTabs] = useState([]);
  const [admitAdjustmentNeedRanks, setAdmitAdjustmentNeedRanks] = useState([]);
  const [admitAdjustmentUserInput, setAdmitAdjustmentUserInput] = useState({});

  const [loading, setLoading] = useState({
    model: false,
    nonModeledData: true,
    admitAdjustments: true,
  });
  const [isSaving, setIsSaving] = useState({
    nonModeledData: false,
    admitAdjustments: false,
  });
  const [isSaved, setIsSaved] = useState({
    nonModeledData: false,
    admitAdjustments: false,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("Non-Modeled Aid Results");

  const firstYearEstimate = useMemo(() => {
    if (!modelConfig || !modelConfig.aidResults?.generated) return [];
    const { aidResults } = modelConfig;
    const initialState = {
      group: "First Year Estimate",
      estEnrollment: aidResults.estEnrollment,
      estInstitutionalAid: aidResults.estInstitutionalAid,
      estNetRevenue: aidResults.estNetRevenue,
      estTuitionDiscountRate: aidResults.estDiscountRate,
    };
    const summedData = nonModeledData.adjusted.reduce((acc, item) => {
      acc.estEnrollment += item.estEnrollment;
      acc.estInstitutionalAid += item.estInstitutionalAid;
      acc.estNetRevenue += item.estNetRevenue;
      return acc;
    }, initialState);
    const grossRevenue =
      summedData.estNetRevenue + summedData.estInstitutionalAid;
    // if gross revenue is 0, set tuition discount rate to 0 to avoid division by 0
    summedData.estTuitionDiscountRate =
      grossRevenue === 0
        ? "0.0000"
        : (summedData.estInstitutionalAid / grossRevenue).toFixed(4);
    return [summedData];
  }, [nonModeledData?.adjusted, modelConfig?.aidResults]);

  return (
    <FaoResultsContext.Provider
      value={{
        firstYearEstimate,
        nonModeledData,
        nonModeledDispatch,
        costOfAttendance,
        costOfAttendanceDispatch,
        // Admit adjustment data
        admitAdjustmentData,
        setAdmitAdjustmentData,
        admitAdjustmentTabs,
        setAdmitAdjustmentTabs,
        admitAdjustmentNeedRanks,
        setAdmitAdjustmentNeedRanks,
        admitAdjustmentUserInput,
        setAdmitAdjustmentUserInput,
        loading,
        setLoading,
        activeTab,
        setActiveTab,
        isSaving,
        setIsSaving,
        isSaved,
        setIsSaved,
        isExporting,
        setIsExporting,
      }}
    >
      {children}
    </FaoResultsContext.Provider>
  );
}
